import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Tv, Play, ExternalLink, ChevronDown, ChevronUp, X, Maximize2, Download } from 'lucide-react';
import { Button } from './ui/button';
import { useTheme } from './ThemeToggle';

// Chaînes TV avec flux YouTube Live
const LIVE_CHANNELS = [
  { id: 'france24', name: 'France 24', logo: '🌍', color: '#00A2E8', youtubeId: 'l8PMl7tUDIE', description: 'Info internationale' },
  { id: 'euronews', name: 'Euronews', logo: '🇪🇺', color: '#003399', youtubeId: 'NiRIbKwAejk', description: 'Info européenne' },
  { id: 'tv5monde', name: 'TV5 Monde', logo: '🌐', color: '#E30613', url: 'https://information.tv5monde.com/direct', description: 'Francophonie' },
  { id: 'arte', name: 'Arte', logo: '🎭', color: '#FF6B00', url: 'https://www.arte.tv/fr/direct/', description: 'Culture & Docs' },
  { id: 'lofi', name: 'Lofi Radio', logo: '🎵', color: '#9B59B6', youtubeId: 'jfKfPfyJRdk', description: 'Musique relaxante' },
  { id: 'jazz', name: 'Jazz Radio', logo: '🎷', color: '#2ECC71', youtubeId: 'Dx5qFachd3A', description: 'Jazz & Chill' },
];

// Sites officiels des chaînes françaises
const FRENCH_CHANNELS = [
  { id: 'molotov', name: 'Molotov', logo: '📺', color: '#00D4AA', url: 'https://www.molotov.tv', description: 'TV gratuite' },
  { id: 'tf1', name: 'TF1+', logo: '1️⃣', color: '#E4002B', url: 'https://www.tf1.fr/tf1/direct', description: 'Direct & Replay' },
  { id: 'francetv', name: 'France TV', logo: '🇫🇷', color: '#0055A4', url: 'https://www.france.tv', description: 'France 2, 3, 5...' },
  { id: 'm6', name: 'M6+', logo: '6️⃣', color: '#FF6600', url: 'https://www.6play.fr/m6/direct', description: 'Direct & Replay' },
];

// Plateformes de streaming
const STREAMING_PLATFORMS = [
  { id: 'youtube', name: 'YouTube', logo: '▶️', color: '#FF0000', url: 'https://www.youtube.com' },
  { id: 'tiktok', name: 'TikTok', logo: '🎵', color: '#000000', url: 'https://www.tiktok.com' },
  { id: 'netflix', name: 'Netflix', logo: '🎬', color: '#E50914', url: 'https://www.netflix.com' },
  { id: 'prime', name: 'Prime Video', logo: '📦', color: '#00A8E1', url: 'https://www.primevideo.com' },
  { id: 'disney', name: 'Disney+', logo: '🏰', color: '#113CCF', url: 'https://www.disneyplus.com' },
  { id: 'twitch', name: 'Twitch', logo: '🎮', color: '#9146FF', url: 'https://www.twitch.tv' },
];

// Apps IPTV gratuites
const IPTV_APPS = [
  { id: 'android', name: 'Android', icon: '🤖', color: '#3DDC84', url: 'https://play.google.com/store/apps/details?id=com.sidhbalitech.ninexplayer', store: 'Google Play' },
  { id: 'ios', name: 'iPhone', icon: '🍎', color: '#007AFF', url: 'https://apps.apple.com/fr/app/xp-iptv/id6744047936', store: 'App Store' },
];

export default function TVPlayer({ compact = false }) {
  const [showTV, setShowTV] = useState(true);
  const [activeChannel, setActiveChannel] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { isDarkMode } = useTheme();

  const handleLiveChannelClick = (channel) => {
    if (channel.youtubeId) {
      setActiveChannel(channel);
    } else if (channel.url) {
      window.open(channel.url, '_blank');
    }
  };

  const handleClosePlayer = () => {
    setActiveChannel(null);
    setIsFullscreen(false);
  };

  const handleOpenInYouTube = () => {
    if (activeChannel?.youtubeId) {
      window.open(`https://www.youtube.com/watch?v=${activeChannel.youtubeId}`, '_blank');
    }
  };

  if (compact) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex items-center gap-2 p-2 rounded-xl border bg-indigo-500/10 border-indigo-500/30"
      >
        <Tv className="w-5 h-5 text-indigo-500" />
        <span className="text-sm font-semibold text-white">🛸 Centre de Divertissement</span>
      </motion.div>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl"
        style={{
          background: 'linear-gradient(135deg, #020617 0%, #0f172a 50%, #1e1b4b 100%)',
          boxShadow: '0 0 40px rgba(99, 102, 241, 0.2), 0 15px 30px rgba(0,0,0,0.4)',
          border: '1px solid rgba(99, 102, 241, 0.3)'
        }}
      >
        {/* Étoiles animées */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 rounded-full bg-indigo-400"
              style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, opacity: Math.random() * 0.3 + 0.1 }}
              animate={{ opacity: [0.1, 0.5, 0.1], scale: [1, 1.3, 1] }}
              transition={{ duration: 2 + Math.random() * 2, repeat: Infinity, delay: Math.random() * 2 }}
            />
          ))}
        </div>

        {/* Bordure supérieure */}
        <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-indigo-500/50 to-transparent" />
        
        {/* HUD Corners */}
        <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-indigo-500/50" />
        <div className="absolute top-2 right-2 w-3 h-3 border-t-2 border-r-2 border-indigo-500/50" />
        <div className="absolute bottom-2 left-2 w-3 h-3 border-b-2 border-l-2 border-indigo-500/30" />
        <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-indigo-500/30" />

        {/* Header */}
        <div className="relative p-4 flex items-center justify-between border-b border-indigo-500/20">
          <div className="flex items-center gap-3">
            <motion.div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(99, 102, 241, 0.1) 100%)',
                border: '1px solid rgba(99, 102, 241, 0.3)'
              }}
              animate={{ boxShadow: ['0 0 10px rgba(99, 102, 241, 0.2)', '0 0 20px rgba(99, 102, 241, 0.4)', '0 0 10px rgba(99, 102, 241, 0.2)'] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <span className="text-xl">📺</span>
            </motion.div>
            <div>
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                Centre Multimédia
                <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-indigo-500/20 text-indigo-400 border border-indigo-500/30">
                  ORBITAL
                </span>
              </h3>
              <p className="text-[11px] text-indigo-500/70 font-mono tracking-wider">
                TV • STREAMING • IPTV
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <motion.div
              className="w-2 h-2 rounded-full bg-indigo-500"
              animate={{ opacity: [1, 0.5, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
            />
            <button
              onClick={() => setShowTV(!showTV)}
              className="p-2 rounded-lg text-gray-400 hover:text-indigo-400 hover:bg-indigo-500/10 transition-all"
            >
              {showTV ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {showTV && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="relative p-4">
                {/* Lecteur intégré si une chaîne est active */}
                {activeChannel && !isFullscreen && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="mb-4 rounded-xl overflow-hidden"
                    style={{ border: '2px solid rgba(6, 182, 212, 0.5)', boxShadow: '0 0 30px rgba(6, 182, 212, 0.3)' }}
                  >
                    <div className="bg-black/80 p-2 flex items-center justify-between border-b border-cyan-500/30">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{activeChannel.logo}</span>
                        <span className="text-white font-semibold text-sm">{activeChannel.name}</span>
                        <motion.span 
                          className="text-red-500 text-xs font-mono"
                          animate={{ opacity: [1, 0.5, 1] }}
                          transition={{ duration: 1, repeat: Infinity }}
                        >
                          ● LIVE
                        </motion.span>
                      </div>
                      <div className="flex gap-1">
                        <Button onClick={handleOpenInYouTube} size="sm" variant="ghost" className="h-7 px-2 text-xs text-gray-400 hover:text-white">
                          <ExternalLink className="w-3 h-3 mr-1" /> YouTube
                        </Button>
                        <Button onClick={() => setIsFullscreen(true)} size="sm" variant="ghost" className="h-7 w-7 p-0 text-gray-400 hover:text-white">
                          <Maximize2 className="w-4 h-4" />
                        </Button>
                        <Button onClick={handleClosePlayer} size="sm" variant="ghost" className="h-7 w-7 p-0 text-gray-400 hover:text-red-400">
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="aspect-video bg-black relative">
                      <iframe
                        src={`https://www.youtube.com/embed/${activeChannel.youtubeId}?autoplay=1&rel=0&modestbranding=1`}
                        title={activeChannel.name}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  </motion.div>
                )}

                {/* TV Live Internationale */}
                <div className="mb-4">
                  <p className="text-xs mb-3 flex items-center gap-2 text-indigo-400/70 font-mono">
                    <span className="text-base">🌍</span> TRANSMISSIONS LIVE
                    <span className="text-[10px] px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">EMBED</span>
                  </p>
                  <div className="grid grid-cols-3 sm:grid-cols-3 gap-2">
                    {LIVE_CHANNELS.map((channel, idx) => (
                      <motion.button
                        key={channel.id}
                        onClick={() => handleLiveChannelClick(channel)}
                        className={`p-3 rounded-xl text-center transition-all relative ${activeChannel?.id === channel.id ? 'ring-2 ring-cyan-400' : ''}`}
                        style={{ background: 'rgba(0,0,0,0.3)', border: `1px solid ${channel.color}40` }}
                        whileHover={{ scale: 1.05, boxShadow: `0 0 20px ${channel.color}40` }}
                        whileTap={{ scale: 0.95 }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                      >
                        {channel.youtubeId && (
                          <motion.div 
                            className="absolute top-1 right-1"
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ duration: 1, repeat: Infinity }}
                          >
                            <Play className="w-3 h-3 text-red-500" />
                          </motion.div>
                        )}
                        <span className="text-xl block mb-1">{channel.logo}</span>
                        <p className="text-xs font-semibold text-white">{channel.name}</p>
                        <p className="text-[10px] mt-0.5 text-gray-500">{channel.description}</p>
                      </motion.button>
                    ))}
                  </div>
                </div>

                {/* Séparateur spatial */}
                <div className="my-4 h-px bg-gradient-to-r from-transparent via-indigo-500/30 to-transparent" />

                {/* TV Française */}
                <div className="mb-4">
                  <p className="text-xs mb-3 flex items-center gap-2 text-indigo-400/70 font-mono">
                    <span className="text-base">🇫🇷</span> CHAÎNES TERRESTRES
                  </p>
                  <div className="grid grid-cols-4 gap-2">
                    {FRENCH_CHANNELS.map((channel, idx) => (
                      <motion.button
                        key={channel.id}
                        onClick={() => window.open(channel.url, '_blank')}
                        className="p-2 rounded-xl text-center transition-all relative group"
                        style={{ background: 'rgba(0,0,0,0.3)', border: `1px solid ${channel.color}30` }}
                        whileHover={{ scale: 1.05, boxShadow: `0 0 15px ${channel.color}30` }}
                        whileTap={{ scale: 0.95 }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                      >
                        <ExternalLink className="w-2.5 h-2.5 absolute top-1 right-1 text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                        <span className="text-lg block">{channel.logo}</span>
                        <p className="text-[10px] font-semibold text-white">{channel.name}</p>
                      </motion.button>
                    ))}
                  </div>
                </div>

                {/* Séparateur spatial */}
                <div className="my-4 h-px bg-gradient-to-r from-transparent via-indigo-500/30 to-transparent" />

                {/* Streaming */}
                <div className="mb-4">
                  <p className="text-xs mb-3 flex items-center gap-2 text-indigo-400/70 font-mono">
                    <span className="text-base">🎬</span> PLATEFORMES STREAMING
                  </p>
                  <div className="grid grid-cols-6 gap-2">
                    {STREAMING_PLATFORMS.map((platform, idx) => (
                      <motion.button
                        key={platform.id}
                        onClick={() => window.open(platform.url, '_blank')}
                        className="p-2 rounded-xl text-center transition-all"
                        style={{ background: 'rgba(0,0,0,0.3)', border: `1px solid ${platform.color}30` }}
                        whileHover={{ scale: 1.08, boxShadow: `0 0 15px ${platform.color}40` }}
                        whileTap={{ scale: 0.95 }}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: idx * 0.05 }}
                      >
                        <span className="text-xl block mb-0.5">{platform.logo}</span>
                        <p className="text-[9px] font-semibold text-white">{platform.name}</p>
                      </motion.button>
                    ))}
                  </div>
                </div>

                {/* Séparateur spatial */}
                <div className="my-4 h-px bg-gradient-to-r from-transparent via-indigo-500/30 to-transparent" />

                {/* IPTV Gratuit */}
                <div>
                  <p className="text-xs mb-3 flex items-center gap-2 text-indigo-400/70 font-mono">
                    <span className="text-base">📡</span> MODULE IPTV
                    <span className="text-[10px] px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">+1000 CHAÎNES</span>
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {IPTV_APPS.map((app, idx) => (
                      <motion.a
                        key={app.id}
                        href={app.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 rounded-xl transition-all"
                        style={{ background: 'rgba(0,0,0,0.4)', border: `1px solid ${app.color}30` }}
                        whileHover={{ scale: 1.02, boxShadow: `0 4px 20px ${app.color}30` }}
                        whileTap={{ scale: 0.98 }}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.1 }}
                      >
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl" style={{ background: `${app.color}20`, border: `1px solid ${app.color}30` }}>
                          {app.icon}
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-sm text-white">XP IPTV</p>
                          <p className="text-[10px]" style={{ color: app.color }}>{app.store}</p>
                        </div>
                        <motion.div animate={{ y: [0, -2, 0] }} transition={{ duration: 1, repeat: Infinity }}>
                          <Download className="w-4 h-4" style={{ color: app.color }} />
                        </motion.div>
                      </motion.a>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Footer HUD */}
        <div className="px-4 py-2 border-t border-indigo-500/10 flex items-center justify-between">
          <span className="text-[10px] font-mono text-gray-600">📺 MEDIA CENTER v2.0</span>
          <div className="flex items-center gap-1">
            <motion.div className="w-1.5 h-1.5 rounded-full bg-indigo-500" animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 1, repeat: Infinity }} />
            <div className="w-1.5 h-1.5 rounded-full bg-indigo-400/50" />
            <div className="w-1.5 h-1.5 rounded-full bg-indigo-300/30" />
          </div>
        </div>
      </motion.div>

      {/* Modal plein écran */}
      <AnimatePresence>
        {isFullscreen && activeChannel && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black flex flex-col"
          >
            <div className="bg-gradient-to-r from-gray-900 to-indigo-900/50 p-3 flex items-center justify-between border-b border-indigo-500/30">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{activeChannel.logo}</span>
                <div>
                  <span className="text-white font-bold">{activeChannel.name}</span>
                  <motion.span className="text-red-500 text-sm ml-2" animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 1, repeat: Infinity }}>● EN DIRECT</motion.span>
                </div>
              </div>
              <Button onClick={handleClosePlayer} variant="ghost" className="text-white hover:text-red-400">
                <X className="w-6 h-6" />
              </Button>
            </div>
            <div className="flex-1 bg-black">
              <iframe
                src={`https://www.youtube.com/embed/${activeChannel.youtubeId}?autoplay=1&rel=0`}
                title={activeChannel.name}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
